import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'

interface PrintPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  documentType: 'INVOICE' | 'PURCHASE_INVOICE' | 'EXPENSE' | 'PAYMENT'
  documentId: string
  printFormatId?: string
}

const PrintPreviewModal: React.FC<PrintPreviewModalProps> = ({
  isOpen,
  onClose,
  documentType,
  documentId,
  printFormatId,
}) => {
  const [previewHtml, setPreviewHtml] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen && documentId) {
      fetchPreview()
    }
  }, [isOpen, documentId, printFormatId])

  const fetchPreview = async () => {
    try {
      setIsLoading(true)
      const params: any = {}
      if (printFormatId) {
        params.printFormatId = printFormatId
      }
      const response = await axios.get(
        `/api/erp/print-preview/${documentType}/${documentId}`,
        { params, responseType: 'text' }
      )
      setPreviewHtml(response.data)
    } catch (error: any) {
      console.error('Error fetching print preview:', error)
      toast.error(error.response?.data?.error || 'Failed to load print preview')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePrint = () => {
    const printWindow = window.open('', '_blank')
    if (printWindow) {
      printWindow.document.write(previewHtml)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 250)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-bold">Print Preview</h2>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              disabled={isLoading || !previewHtml}
            >
              Print
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : previewHtml ? (
            <div
              className="bg-white"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          ) : (
            <div className="text-center text-gray-500 py-12">
              No preview available
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PrintPreviewModal
